/**
 * On-screen touch controls (mobile). DOM buttons over the canvas that feed the SAME
 * Input command channel as the keyboard (Input.setVirtual / toggleRun), so the
 * deterministic sim is untouched.
 *
 * Restyled to the sticker-cartoon system (handoff §04 Play states): the buttons are
 * solid sticker shapes (ink outline + hard offset shadow), not Kenney art — these are
 * UI chrome, not game sprites. Scheme:
 *   left  : ◀ / ▶ d-pad (held)                       (rounded sticker squares)
 *   right : DUCK (sm) · RUN (toggle, blue) · JUMP (lg, green primary)   (round)
 *   above : per-archetype action cluster — combat ATK/DASH, dash-precision DASH/GRAB
 * RUN defaults ON (auto-run) so the demo's running-jump gaps clear out of the box.
 *
 * Dash-precision keeps the analog joystick for its 8-way dash aim (the design doesn't
 * cover it); everything else uses the sticker d-pad. Pointer capture + touch-action:none
 * so a finger that slides off still releases and dragging never scrolls the page.
 */
import type { Action, Input } from '../engine/Input';
import type { ArchetypeId } from '../engine/Archetype';
import JOY_PAD_URL from '../assets/mobile/joystick_pad.png?url';
import JOY_NUB_URL from '../assets/mobile/joystick_nub.png?url';

type StickerColor = 'cream' | 'green' | 'blue' | 'gold' | 'red';
type Shape = 'rect' | 'round';

const FILL: Record<StickerColor, { bg: string; fg: string }> = {
  cream: { bg: '#fff8ec', fg: '#2b2335' },
  green: { bg: 'linear-gradient(180deg,#8ad94f,#5fb22a)', fg: '#fff' },
  blue:  { bg: 'linear-gradient(180deg,#5cb6f7,#2b8de0)', fg: '#fff' },
  gold:  { bg: 'linear-gradient(180deg,#ffd95e,#f3a81c)', fg: '#2b2335' },
  red:   { bg: 'linear-gradient(180deg,#f56a5b,#d8362a)', fg: '#fff' },
};

/** A per-archetype action button (the second right-side cluster above run/jump). */
interface ActionBtn { action: Action; color: StickerColor; size: number; label: string }

const ACTION_BUTTONS: Partial<Record<ArchetypeId, ActionBtn[]>> = {
  combat: [
    { action: 'dash', color: 'blue', size: 76, label: 'DASH' },
    { action: 'attack', color: 'red', size: 92, label: 'ATK' },
  ],
  dashprecision: [
    { action: 'dash', color: 'blue', size: 84, label: 'DASH' },
    { action: 'grab', color: 'gold', size: 84, label: 'GRAB' },
  ],
  // AUTO-RUNNER: horizontal is automatic → no move pad. DIVE = hold duck in the air (reuses the
  // `duck` action/input; the sim's dive branch reads it). JUMP stays the base right-cluster
  // primary, so it is NOT duplicated here.
  autorunner: [
    { action: 'duck', color: 'gold', size: 84, label: 'DIVE' },
  ],
  // RUN-AND-GUN: a FIRE button on the `attack` action (held → Input.isDown('attack') →
  // InputSnapshot.fireHeld → the Player's held auto-fire). Movement is the normal ◀ ▶ d-pad, so
  // touch aim = the held d-pad direction (axisX) else facing; DUCK held aims down, no up on touch
  // (keyboard gets the full 8-way). Same held-button trick AUTO-RUNNER used for DIVE.
  runandgun: [
    { action: 'attack', color: 'red', size: 92, label: 'FIRE' },
  ],
};

/** Archetypes whose dash is 8-way → the movement pad is an analog joystick. */
const JOYSTICK_ARCHETYPES = new Set<ArchetypeId>(['dashprecision']);

/** Archetypes with AUTOMATIC horizontal movement → no ◀ ▶ / joystick move pad and no DUCK/RUN
 *  in the right cluster (AUTO-RUNNER). The big JUMP + a DIVE action are the whole scheme. */
const NO_MOVEMENT_ARCHETYPES = new Set<ArchetypeId>(['autorunner']);

/**
 * A phone/tablet by USER-AGENT — used only to reveal the pad IMMEDIATELY at boot so
 * obvious mobile devices see controls before their first touch. Everything else relies
 * on actual-input detection (see enableAutoToggle).
 */
export function isMobileUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iPadOS = /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) || iPadOS;
}

export class TouchControls {
  private root = document.createElement('div');
  private runBtn!: HTMLButtonElement;
  private revealed = false;
  private autoToggle = false;
  private readonly onGlobalPointer = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') this.reveal();
    else if (e.pointerType === 'mouse') this.conceal();
  };
  private readonly onGlobalKey = (): void => this.conceal();

  constructor(private input: Input, archetypeId: ArchetypeId = 'classic') {
    Object.assign(this.root.style, {
      position: 'fixed', inset: '0', zIndex: '1000',
      pointerEvents: 'none', touchAction: 'none', userSelect: 'none', display: 'none',
    } as CSSStyleDeclaration);
    (this.root.style as CSSStyleDeclaration & { webkitUserSelect: string }).webkitUserSelect = 'none';

    const isJoystick = JOYSTICK_ARCHETYPES.has(archetypeId);
    const isNoMovement = NO_MOVEMENT_ARCHETYPES.has(archetypeId);

    // Movement: AUTO-RUNNER has none (horizontal is automatic); joystick for 8-way-dash
    // archetypes; else the ◀ ▶ sticker d-pad.
    if (isNoMovement) {
      // no movement cluster — the player cruises automatically
    } else if (isJoystick) {
      this.root.append(this.buildJoystick({ left: '16px', bottom: '16px' }, 150));
    } else {
      const left = this.cluster({ left: '16px', bottom: '16px' });
      left.append(
        this.holdButton('left', 'rect', 'cream', 76, '◀'),
        this.holdButton('right', 'rect', 'cream', 76, '▶'),
      );
      this.root.append(left);
    }

    // Right cluster: DUCK (only on the d-pad scheme — joystick ducks via down; auto-runner's
    // duck is the DIVE action below) · RUN (toggle — omitted for auto-runner, the cruise speed is
    // fixed) · JUMP (primary, the biggest clear-of-the-thumb target — always present).
    const right = this.cluster({ right: '16px', bottom: '16px' });
    if (!isJoystick && !isNoMovement) right.append(this.holdButton('duck', 'round', 'cream', 64, 'DUCK'));
    this.runBtn = this.toggleRunButton(76); // constructed always so reveal()'s markRun is safe
    if (!isNoMovement) right.append(this.runBtn);
    right.append(this.holdButton('jump', 'round', 'green', 96, 'JUMP'));
    this.root.append(right);

    // Per-archetype action buttons: a second right-side cluster ABOVE run/jump.
    const actions = ACTION_BUTTONS[archetypeId];
    if (actions) {
      const cluster = this.cluster({ right: '16px', bottom: '128px' });
      for (const a of actions) cluster.append(this.holdButton(a.action, 'round', a.color, a.size, a.label));
      this.root.append(cluster);
    }

    document.body.appendChild(this.root);
  }

  // ── sticker button factory ──────────────────────────────────────────────
  private baseButton(shape: Shape, color: StickerColor, size: number, label: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    const { bg, fg } = FILL[color];
    Object.assign(b.style, {
      width: `${size}px`, height: `${size}px`, padding: '0', cursor: 'pointer',
      background: bg, color: fg,
      border: '3px solid #2b2335',
      borderRadius: shape === 'round' ? '50%' : '16px',
      boxShadow: '0 5px 0 #2b2335',
      display: 'grid', placeItems: 'center',
      font: `700 ${Math.max(12, Math.round(size * 0.2))}px 'Fredoka', system-ui, sans-serif`,
      textShadow: color === 'cream' || color === 'gold' ? 'none' : '0 1.5px 0 rgba(43,35,53,0.4)',
      touchAction: 'none', userSelect: 'none',
      transition: 'transform 0.06s, box-shadow 0.06s, filter 0.06s',
    } as CSSStyleDeclaration);
    b.style.setProperty('-webkit-tap-highlight-color', 'transparent');
    // Glyphs (◀▶) read larger than text labels.
    if (label === '◀' || label === '▶') b.style.fontSize = `${Math.round(size * 0.4)}px`;
    b.textContent = label;
    return b;
  }

  private cluster(pos: Partial<CSSStyleDeclaration>): HTMLDivElement {
    const c = document.createElement('div');
    Object.assign(c.style, {
      position: 'absolute', display: 'flex', gap: '12px', alignItems: 'flex-end',
      pointerEvents: 'auto', ...pos,
    } as CSSStyleDeclaration);
    return c;
  }

  /** A held button (◀ ▶ DUCK JUMP ATK …): down = setVirtual true, release = false. */
  private holdButton(action: Action, shape: Shape, color: StickerColor, size: number, label: string): HTMLButtonElement {
    const b = this.baseButton(shape, color, size, label);
    const press = (on: boolean): void => {
      b.style.transform = on ? 'translateY(4px)' : 'translateY(0)';
      b.style.boxShadow = on ? '0 1px 0 #2b2335' : '0 5px 0 #2b2335';
    };
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      try { b.setPointerCapture(e.pointerId); } catch { /* synthetic / already released */ }
      this.input.setVirtual(action, true);
      press(true);
    });
    const release = (): void => { this.input.setVirtual(action, false); press(false); };
    b.addEventListener('pointerup', release);
    b.addEventListener('pointercancel', release);
    b.addEventListener('lostpointercapture', release);
    return b;
  }

  /** RUN toggle — taps flip the sprint latch; highlight reflects state. */
  private toggleRunButton(size: number): HTMLButtonElement {
    const b = this.baseButton('round', 'blue', size, 'RUN');
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      const on = this.input.toggleRun();
      this.markRun(b, on);
    });
    return b;
  }

  private markRun(b: HTMLButtonElement, on: boolean): void {
    b.style.filter = on ? 'saturate(1.35) brightness(1.06)' : 'saturate(0.85) brightness(0.95)';
  }

  /**
   * An 8-way analog joystick (Kenney pad + nub) for dash-precision: drives movement
   * axisX AND the dash aim (left/right/aimUp/duck). Pointer-captured; resets on release.
   */
  private buildJoystick(pos: Partial<CSSStyleDeclaration>, size: number): HTMLDivElement {
    const nubSize = Math.round(size * 0.46);
    const radius = (size - nubSize) / 2;
    const pad = document.createElement('div');
    Object.assign(pad.style, {
      position: 'absolute', width: `${size}px`, height: `${size}px`, pointerEvents: 'auto',
      touchAction: 'none', userSelect: 'none', opacity: '0.85',
      background: `center/contain no-repeat url("${JOY_PAD_URL}")`, ...pos,
    } as CSSStyleDeclaration);
    const nub = document.createElement('div');
    Object.assign(nub.style, {
      position: 'absolute', width: `${nubSize}px`, height: `${nubSize}px`,
      left: `${radius}px`, top: `${radius}px`, pointerEvents: 'none',
      background: `center/contain no-repeat url("${JOY_NUB_URL}")`,
      transition: 'left 0.04s, top 0.04s',
    } as CSSStyleDeclaration);
    pad.appendChild(nub);

    const TH = 0.38;
    const apply = (nx: number, ny: number): void => {
      const mag = Math.hypot(nx, ny) || 1;
      const cnx = mag > 1 ? nx / mag : nx, cny = mag > 1 ? ny / mag : ny;
      nub.style.left = `${radius + cnx * radius}px`;
      nub.style.top = `${radius + cny * radius}px`;
      const r = nx > TH, l = nx < -TH, d = ny > TH, u = ny < -TH;
      this.input.setVirtual('right', r && !l);
      this.input.setVirtual('left', l && !r);
      this.input.setVirtual('aimUp', u && !d);
      this.input.setVirtual('duck', d && !u);
    };
    const reset = (): void => {
      nub.style.left = `${radius}px`; nub.style.top = `${radius}px`;
      this.input.setVirtual('right', false); this.input.setVirtual('left', false);
      this.input.setVirtual('aimUp', false); this.input.setVirtual('duck', false);
    };
    const onMove = (e: PointerEvent): void => {
      const rect = pad.getBoundingClientRect();
      const nx = (e.clientX - (rect.left + rect.width / 2)) / radius;
      const ny = (e.clientY - (rect.top + rect.height / 2)) / radius;
      apply(Math.max(-1.4, Math.min(1.4, nx)), Math.max(-1.4, Math.min(1.4, ny)));
    };
    pad.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      try { pad.setPointerCapture(e.pointerId); } catch { /* synthetic / already released */ }
      onMove(e);
    });
    pad.addEventListener('pointermove', (e) => { if (pad.hasPointerCapture(e.pointerId)) onMove(e); });
    pad.addEventListener('pointerup', reset);
    pad.addEventListener('pointercancel', reset);
    pad.addEventListener('lostpointercapture', reset);
    return pad;
  }

  /** Show the pad. First reveal turns on auto-run so the gaps clear out of the box. */
  reveal(): void {
    this.root.style.display = 'block';
    if (!this.revealed) {
      this.revealed = true;
      this.input.setRunLatched(true);
      this.markRun(this.runBtn, true);
    }
  }
  conceal(): void { this.root.style.display = 'none'; }

  /** Drive reveal/conceal by the input modality actually in use. */
  enableAutoToggle(): void {
    if (this.autoToggle) return;
    this.autoToggle = true;
    window.addEventListener('pointerdown', this.onGlobalPointer, true);
    window.addEventListener('keydown', this.onGlobalKey, true);
  }

  destroy(): void {
    window.removeEventListener('pointerdown', this.onGlobalPointer, true);
    window.removeEventListener('keydown', this.onGlobalKey, true);
    this.root.remove();
  }
}
